import { useTranslation } from "react-i18next";
import { useTheme } from "../../theme/themeContext";
import type { AppTheme } from "../../theme/themeTypes";
import { APP_THEMES } from "../../theme/themeTypes";
import { classNames } from "../styles/classNames";
import OptionCard from "../forms/OptionCard";

interface LanguageThemeControlsProps {
  className?: string;
  compact?: boolean;
  subtle?: boolean;
  showLabels?: boolean;
  showSymbols?: boolean;
  slim?: boolean;
  layout?: "grid" | "inline";
}

const THEME_LABEL_KEY: Record<AppTheme, string> = {
  light: "app.themeLight",
  dark: "app.themeDark",
  retro: "app.themeRetro",
  neon: "app.themeNeon",
};

export default function LanguageThemeControls({
  className,
  compact = false,
  subtle = false,
  showLabels = true,
  showSymbols = true,
  slim = false,
  layout = "grid",
}: LanguageThemeControlsProps) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();

  const language = i18n.resolvedLanguage?.startsWith("de") ? "de" : "en";
  const selectClass = classNames(
    "app-input app-select app-option-card-select",
    layout === "grid" ? "w-full" : "",
    compact ? "app-input-compact text-xs" : ""
  );
  const labelClass = classNames(
    "app-text-muted app-option-card-label text-xs font-medium",
    subtle ? "opacity-85" : ""
  );
  const symbol = (kind: "language" | "theme") =>
    showSymbols ? <span className={`app-control-symbol app-control-symbol-${kind}`} aria-hidden={true} /> : undefined;

  return (
    <div
      className={classNames(
        "app-option-cards",
        layout === "inline" ? "app-option-cards-inline" : "app-option-cards-grid",
        subtle && "app-option-cards-subtle",
        slim && "app-option-cards-slim",
        className
      )}
    >
      <OptionCard
        label={t("app.language")}
        symbol={symbol("language")}
        showLabel={showLabels}
        labelClassName={labelClass}
        selectClassName={selectClass}
        value={language}
        options={[
          { value: "de", label: t("app.german") },
          { value: "en", label: t("app.english") },
        ]}
        onChange={(next) => void i18n.changeLanguage(next)}
      />
      <OptionCard
        label={t("app.theme")}
        symbol={symbol("theme")}
        showLabel={showLabels}
        labelClassName={labelClass}
        selectClassName={selectClass}
        value={theme}
        options={APP_THEMES.map((themeName) => ({ value: themeName, label: t(THEME_LABEL_KEY[themeName]) }))}
        onChange={setTheme}
      />
    </div>
  );
}
